import { Injectable } from '@angular/core';
import { filter, first, firstValueFrom } from 'rxjs';
import { NewProTodoList, ProTodoList, TodoList, UserTodoList } from '../models/todo-list';
import { companyRequiresEidsTodo } from '../utils/eids.utils';
import { EidsService } from './eids.service';
import { CompanyActivity } from '../models/company';
import { User } from '../models/user';
import { AddressService } from './address.service';
import { AuthService } from './auth.service';
import { CompanyService } from './company.service';
import { MediaService } from './media.service';
import { WalletService } from './wallet.service';

@Injectable({
    providedIn: 'root',
})
export class TodoListService {
    constructor(
        private readonly authService: AuthService,
        private readonly companyService: CompanyService,
        private readonly mediaService: MediaService,
        private readonly walletService: WalletService,
        private readonly addressService: AddressService,
        private readonly eidsService: EidsService,
    ) {}

    async createProTodoList(): Promise<ProTodoList> {
        return new Promise<ProTodoList>(async (resolve, reject) => {
            this.authService.userLoggedIn
                .pipe(
                    filter((user) => !!user),
                    first(),
                )
                .subscribe({
                    next: async (user) => {
                        const [company, documents, ibanResult, addresses] = await Promise.all([
                            firstValueFrom(this.companyService.get(user.company)),
                            firstValueFrom(this.mediaService.getDocuments(user.company)),
                            firstValueFrom(this.walletService.getIban()),
                            firstValueFrom(this.companyService.getAddresses(user.company)),
                        ]);
                        const eids = companyRequiresEidsTodo(company.activity) ? await this.eidsFlags() : null;
                        resolve(new ProTodoList(user, company, documents, ibanResult.iban, addresses, eids));
                    },
                });
        });
    }

    async createNewProAccountTodoList() {
        return new Promise<NewProTodoList>(async (resolve, reject) => {
            this.authService.userLoggedIn
                .pipe(
                    filter((user) => !!user),
                    first(),
                )
                .subscribe({
                    next: async (user) => {
                        resolve(new NewProTodoList(user));
                    },
                });
        });
    }

    createUserTodoList(activity: CompanyActivity | null = null, eids: { gsmVerified: boolean; linked: boolean } | null = null) {
        return new Promise<UserTodoList>(async (resolve, reject) => {
            this.authService.userLoggedIn
                .pipe(
                    filter((user) => !!user),
                    first(),
                )
                .subscribe({
                    next: async (user) => {
                        const [ibanResult, addresses] = await Promise.all([
                            firstValueFrom(this.walletService.getIban()),
                            firstValueFrom(this.addressService.getAddresses(user.id)),
                        ]);
                        resolve(new UserTodoList(user, ibanResult.iban, addresses, activity, eids));
                    },
                });
        });
    }

    async create(user: User): Promise<TodoList> {
        const todoList = await new Promise<TodoList>(async (resolve, reject) => {
            if (user.pro) {
                return resolve(await this.createProTodoList());
            }
            if (user.needProAccount) {
                return resolve(await this.createNewProAccountTodoList());
            }
            const { activity, eids } = await this.teamEids(user);
            return resolve(this.createUserTodoList(activity, eids));
        });
        todoList.compute();
        return todoList;
    }

    private async teamEids(user: User): Promise<{ activity: CompanyActivity | null; eids: { gsmVerified: boolean; linked: boolean } | null }> {
        if (user.type !== 'team' || !user.company) {
            return { activity: null, eids: null };
        }
        try {
            const company = await firstValueFrom(this.companyService.get(user.company));
            if (!companyRequiresEidsTodo(company?.activity)) {
                return { activity: company?.activity ?? null, eids: null };
            }
            return { activity: company.activity, eids: await this.eidsFlags() };
        } catch {
            return { activity: null, eids: null };
        }
    }

    private async eidsFlags(): Promise<{ gsmVerified: boolean; linked: boolean } | null> {
        try {
            const status = await firstValueFrom(this.eidsService.status());
            if (!status.required) {
                return null;
            }
            return { gsmVerified: status.gsmVerified, linked: status.linked };
        } catch {
            return null;
        }
    }
}
